import React from 'react';
import { twMerge } from 'tailwind-merge';
import { UserContent } from './UserContent';

export interface CardProps extends Omit<React.HTMLAttributes<HTMLDivElement>, 'title'> {
  variant?: 'default' | 'elevated' | 'bordered' | 'interactive';
  /** Background surface token (Spec 240). Orthogonal to `variant` (which controls
   *  radius/border/shadow). Default 'card' = bg-bg-card — back-compatible with all
   *  existing consumers. Use 'secondary'/'surface'/'elevated' for nested panels. */
  tone?: 'card' | 'secondary' | 'surface' | 'elevated';
  /** Corner radius. Default 'xl' (rounded-xl, back-compat). Use 'lg' for panels that
   *  were rounded-lg pre-migration so they keep their exact look (Spec 240). */
  radius?: 'lg' | 'xl';
  padding?: 'none' | 'sm' | 'md' | 'lg';
  /** Border color token — applies only when the card renders a border (variant
   *  'bordered' or 'interactive'). Default 'light' is back-compat with all existing
   *  Cards. Use 'medium' or 'default' to match raw-div consumers that use
   *  border-border-medium / border-border-default. Exactly three tokens exist in
   *  the codebase: border-light (787 uses), border-medium (81), border-default (78). */
  borderColor?: 'light' | 'medium' | 'default';
  /** Standard hover affordance for non-onClick cards that are visually interactive
   *  (e.g. list rows containing an inner link, selection grids).
   *
   *  `'border'` → `hover:border-border-medium transition-colors`
   *    Use with bordered variants — lifts the border from -light to -medium on hover.
   *    Maps to residuals like DropDetectionHistory (border card + hover border-lift).
   *
   *  `'shadow'` → `hover:shadow-md transition-shadow`
   *    Use with default/elevated variants — lifts the card shadow on hover.
   *    Maps to residuals like KanbanCard (shadow-lift) and ModerationQueuePage.
   *
   *  For bespoke hovers (hover:bg-surface, CSS var tokens, multiple utilities) pass
   *  them via `className` instead — those are too caller-specific to enumerate here.
   *
   *  Do NOT combine with `variant="interactive"` — that variant already owns
   *  `hover:border-accent`; adding `hover="border"` on top creates conflicting hovers. */
  hover?: 'border' | 'shadow';
  className?: string;
  onClick?: () => void;
  /** Card anatomy (contract §7): category eyebrow · title · description · meta row.
   *  All optional — existing children-only consumers are unaffected.
   *  Do NOT place interactive elements (links/buttons) in anatomy slots when
   *  `onClick` is set — the card becomes role="button" and nested interactive
   *  content breaks keyboard navigation (WCAG 2.1.1). */
  eyebrow?: React.ReactNode;
  title?: React.ReactNode;
  /** Render the title in the user-voice serif (user-authored titles only). */
  titleSerif?: boolean;
  description?: React.ReactNode;
  meta?: React.ReactNode;
  children?: React.ReactNode;
}

// variant controls border/shadow ONLY; radius comes from `radius`, bg from `tone` (Spec 240).
// Border-color is injected separately via borderColorClasses so consumers can override it
// without losing the rest of the variant (e.g. bordered cards with border-border-medium).
const variantClasses: Record<NonNullable<CardProps['variant']>, string> = {
  default: '',
  elevated: 'shadow-lg',
  bordered: 'border',
  interactive: 'border hover:border-accent cursor-pointer transition-colors',
};

// Only three border-color tokens exist in the codebase (border-light 787, border-medium 81,
// border-default 78). Support exactly those — no arbitrary token pass-through.
const borderColorClasses: Record<NonNullable<CardProps['borderColor']>, string> = {
  light: 'border-border-light',
  medium: 'border-border-medium',
  default: 'border-border-default',
};

// `hover` prop (design-wave-14): two named values cover the residual raw-div hover patterns.
// 'border' → border-lift to border-medium (e.g. DropDetectionHistory-style bordered lists).
// 'shadow' → shadow-md lift (e.g. KanbanCard / ModerationQueuePage-style elevated cards).
// Bespoke hovers (CSS var tokens, hover:bg-surface + bg changes) stay as className.
// `interactive` variant owns hover:border-accent — don't combine hover="border" with it.
//
// Fan-out rule:
//   • bordered card + hover border-lift        → variant="bordered" hover="border"
//   • default/elevated card + hover shadow-lift → variant="default"  hover="shadow"
//   • bespoke (CSS var, hover:bg-surface, etc.) → className="hover:..."
//   • onClick card (role="button")             → variant="interactive" (no hover prop needed)
const hoverClasses: Record<NonNullable<CardProps['hover']>, string> = {
  border: 'hover:border-border-medium transition-colors',
  shadow: 'hover:shadow-md transition-shadow',
};

// radius controls the corner rounding. 'xl' is the default (back-compat).
const radiusClasses: Record<NonNullable<CardProps['radius']>, string> = {
  lg: 'rounded-lg',
  xl: 'rounded-xl',
};

// tone controls the background surface. 'card' is the default (back-compat).
const toneClasses: Record<NonNullable<CardProps['tone']>, string> = {
  card: 'bg-bg-card',
  secondary: 'bg-bg-secondary',
  surface: 'bg-surface',
  elevated: 'bg-bg-elevated',
};

const paddingClasses: Record<NonNullable<CardProps['padding']>, string> = {
  none: '',
  sm: 'p-3',
  md: 'p-4',
  lg: 'p-6',
};

export const Card = React.forwardRef<HTMLDivElement, CardProps>(
  (
    {
      variant = 'default',
      tone = 'card',
      radius = 'xl',
      padding = 'md',
      borderColor = 'light',
      hover,
      className = '',
      onClick,
      eyebrow,
      title,
      titleSerif = false,
      description,
      meta,
      children,
      ...rest
    },
    ref
  ) => {
    const isInteractive = !!onClick;
    const hasAnatomy = eyebrow != null || title != null || description != null || meta != null;

    // Inject the border-color class only for variants that render a border.
    const borderClass =
      variant === 'bordered' || variant === 'interactive'
        ? borderColorClasses[borderColor]
        : '';

    const hoverClass = hover ? hoverClasses[hover] : '';

    return (
      <div
        ref={ref}
        className={twMerge(radiusClasses[radius], toneClasses[tone], variantClasses[variant], borderClass, paddingClasses[padding], hoverClass, className)}
        onClick={onClick}
        role={isInteractive ? 'button' : undefined}
        tabIndex={isInteractive ? 0 : undefined}
        onKeyDown={
          isInteractive
            ? (e: React.KeyboardEvent) => {
                if (e.key === 'Enter' || e.key === ' ') {
                  e.preventDefault();
                  onClick?.();
                }
              }
            : undefined
        }
        {...rest}
      >
        {hasAnatomy && (
          <>
            {eyebrow != null && (
              <div className="text-[10.5px] font-medium uppercase tracking-[.07em] text-text-tertiary">
                {eyebrow}
              </div>
            )}
            {title != null &&
              (titleSerif ? (
                <UserContent as="h3" className="m-0 mt-0.5 text-text-primary">
                  {title}
                </UserContent>
              ) : (
                <h3 className="m-0 mt-0.5 text-base font-semibold text-text-primary">{title}</h3>
              ))}
            {description != null && (
              <p className="m-0 mt-1 text-sm text-text-secondary">{description}</p>
            )}
            {meta != null && (
              <div className="mt-2 flex items-center gap-2 text-xs text-text-tertiary">{meta}</div>
            )}
          </>
        )}
        {children}
      </div>
    );
  }
);

Card.displayName = 'Card';
